"use client";

import { useRef } from "react";
import { useMembers } from "@zapier/client";
import { isLegacyOwnerId } from "@zapier/utils";

interface Options {
  /** Localized label substituted for the current user's id when matched. */
  meLabel?: string;
  /** Current user's numeric id as a string; resolved as `meLabel` when matched. */
  meId?: string;
}

// Falls back to the raw id for non-numeric values and members the
// account can't resolve. Caches resolved names across renders so adding a
// new owner doesn't briefly flash its raw id while `useMembers` refetches.
export default function useOwnerNameResolver(
  ownerValues: string[],
  options?: Options,
) {
  const cache = useRef(new Map<string, string>());
  const numericIds = ownerValues.filter(isLegacyOwnerId).map(Number);
  const { members } = useMembers({ ids: numericIds });

  for (const id of Object.keys(members)) {
    const name = members[id]?.fullName;
    if (name) cache.current.set(id, name);
  }

  return (id: string): string => {
    if (options?.meLabel && options.meId && id === options.meId) {
      return options.meLabel;
    }
    return cache.current.get(id) ?? id;
  };
}
